import React, { useMemo, useState, useEffect, useRef } from 'react';
import { BASE_URL } from '../../config/constants';
import { WORKSHOP_MODULE_CONTENT_CONFIG } from '../../config/workshopModuleContentConfig';
import { buildWorkshopRenderBlocks } from '../../utils/workshopRenderer';
import prerequisitesData from '../../data/prerequisites.json';
import caseStudiesLibraryData from '../../data/casestudies.json';

const CASE_STUDY_INITIAL = {
  title: '',
  description: '',
  business_problem: '',
  personas: '',
  rules: '',
  acceptance_criteria: '',
};

const CASE_STUDY_FIELDS = [
  {
    key: 'title',
    label: 'Case Title',
    placeholder: 'e.g. Reduce onboarding drop-off',
    help: 'Use a concise action-oriented title that states the business outcome.',
    type: 'input',
    required: true,
    wide: true,
  },
  {
    key: 'description',
    label: 'Description',
    placeholder: 'Briefly describe the context and what changed recently.',
    help: 'Give enough context for someone new to quickly understand the scenario.',
    type: 'textarea',
    rows: 3,
    wide: true,
  },
  {
    key: 'business_problem',
    label: 'Business Problem',
    placeholder: 'What measurable business problem are you solving?',
    help: 'Focus on impact, metrics, and why this matters now.',
    type: 'textarea',
    rows: 4,
  },
  {
    key: 'personas',
    label: 'Personas',
    placeholder: 'Who are the users or stakeholders affected?',
    help: 'List primary and secondary personas with key needs.',
    type: 'textarea',
    rows: 4,
  },
  {
    key: 'rules',
    label: 'Rules & Constraints',
    placeholder: 'List constraints, assumptions, compliance needs, or non-negotiables.',
    help: 'Capture budget, timeline, legal, technical, or policy constraints.',
    type: 'textarea',
    rows: 4,
  },
  {
    key: 'acceptance_criteria',
    label: 'Acceptance Criteria',
    placeholder: 'Define what success looks like and how it will be validated.',
    help: 'Write clear testable outcomes that teams can align on.',
    type: 'textarea',
    rows: 4,
  },
];

const normalizeCaseStudyLibrary = (rawLibrary) => {
  if (!Array.isArray(rawLibrary)) return [];

  return rawLibrary
    .map((item, index) => ({
      id: String(item?.id || `case-${index + 1}`),
      title: String(item?.title || '').trim(),
      description: String(item?.description || '').trim(),
      business_problem: String(item?.business_problem || '').trim(),
      personas: String(item?.personas || '').trim(),
      rules: String(item?.rules || '').trim(),
      acceptance_criteria: String(item?.acceptance_criteria || '').trim(),
      domain: String(item?.domain || '').trim(),
      difficulty: String(item?.difficulty || '').trim(),
    }))
    .filter((item) => item.title);
};

const getLoggedInAttendeeId = () => {
  const rawUserId = localStorage.getItem('userId') || localStorage.getItem('userid') || '';
  const parsed = parseInt(rawUserId, 10);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const getAttendeeToken = () => localStorage.getItem('attendeeToken') || localStorage.getItem('token') || '';

const getWorkshopHeaders = (includeJson = false) => {
  const token = getAttendeeToken();
  return {
    ...(includeJson ? { 'Content-Type': 'application/json' } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const SAMPLE_5W_QUESTION_BANK = [
  {
    key: 'Why',
    questions: [
      {
        text: 'Why does this initiative matter for the business right now?',
        helpText: 'Tie your answer to one measurable business outcome or KPI.',
      },
      {
        text: 'Why is the current approach not enough?',
        helpText: 'Describe the gap, friction, or risk with current behavior.',
      },
    ],
  },
  {
    key: 'What',
    questions: [
      {
        text: 'What problem are we solving in one clear sentence?',
        helpText: 'Keep it concise and outcome-focused.',
      },
      {
        text: 'What does success look like after implementation?',
        helpText: 'Define observable indicators of success.',
      },
    ],
  },
  {
    key: 'Who',
    questions: [
      {
        text: 'Who are the primary users impacted by this change?',
        helpText: 'Mention users, stakeholders, and owners.',
      },
      {
        text: 'Who decides if this solution is acceptable?',
        helpText: 'Identify approvers, sponsors, or governance bodies.',
      },
    ],
  },
  {
    key: 'When',
    questions: [
      {
        text: 'When should this be delivered to create maximum value?',
        helpText: 'Call out timeline windows and milestone expectations.',
      },
      {
        text: 'When will you validate that outcomes are achieved?',
        helpText: 'Define review cadence and checkpoints.',
      },
    ],
  },
  {
    key: 'Where',
    questions: [
      {
        text: 'Where in the journey does the biggest friction happen?',
        helpText: 'Map the exact stage, system, or touchpoint.',
      },
      {
        text: 'Where will this solution be piloted first?',
        helpText: 'Pick a realistic scope for your first rollout.',
      },
    ],
  },
];

const canonicalize5WCategory = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';

  const normalized = raw.toUpperCase();
  const standardMap = {
    WHY: 'Why',
    WHAT: 'What',
    WHO: 'Who',
    WHEN: 'When',
    WHERE: 'Where',
  };

  return standardMap[normalized] || raw;
};

const normalize5WQuestionBank = (rawCategories = []) => {
  if (!Array.isArray(rawCategories)) return [];

  return rawCategories
    .map((category, categoryIndex) => {
      const key = canonicalize5WCategory(category?.key || category?.category);
      if (!key) return null;

      const normalizedQuestions = (Array.isArray(category?.questions) ? category.questions : [])
        .map((question, questionIndex) => {
          if (typeof question === 'string') {
            const text = question.trim();
            return text ? { text, helpText: '' } : null;
          }

          const text = String(
            question?.text || question?.question || question?.question_text || ''
          ).trim();
          const helpText = String(
            question?.helpText || question?.help_text || question?.hint || ''
          ).trim();

          if (!text) {
            return { text: `Question ${questionIndex + 1}`, helpText };
          }

          return { text, helpText };
        })
        .filter(Boolean);

      return {
        key,
        questions: normalizedQuestions,
        order: Number.isFinite(category?.display_order) ? category.display_order : categoryIndex,
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.order - b.order)
    .map(({ key, questions }) => ({ key, questions }));
};

const downloadTextFile = (filename, content) => {
  const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const CopyIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M9 9h10v10H9z" stroke="currentColor" strokeWidth="1.8" />
    <path d="M5 15H4V4h11v1" stroke="currentColor" strokeWidth="1.8" />
  </svg>
);

const DownloadIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M12 4v10" stroke="currentColor" strokeWidth="1.8" />
    <path d="M8 10l4 4 4-4" stroke="currentColor" strokeWidth="1.8" />
    <path d="M5 19h14" stroke="currentColor" strokeWidth="1.8" />
  </svg>
);

const IconActionButton = ({ label, onClick, children }) => (
  <button type="button" className="ep-btn-ghost" onClick={onClick} aria-label={label}>
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      {children}
      <span>{label}</span>
    </span>
  </button>
);

const SDLC_ROLES = [
  'Product Manager',
  'Business Analyst',
  'Solutions Architect',
  'Frontend Developer',
  'Backend Developer',
  'Full Stack Developer',
  'QA Engineer',
  'DevOps Engineer',
  'Scrum Master',
  'Agile Coach',
  'UX / UI Designer',
  'Technical Lead',
  'Project Manager',
  'Security Engineer',
  'Data Engineer',
  'Release Manager',
];

const PromptLabsInline = ({ workshopId, caseStudy, userRole, onRoleChange, onLessonComplete }) => {
  const attendeeid = getLoggedInAttendeeId();
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [answersByKey, setAnswersByKey] = useState({});
  const [existingByKey, setExistingByKey] = useState({});
  const [fetchedByCategory, setFetchedByCategory] = useState({});
  const [threadIdByCategory, setThreadIdByCategory] = useState({});
  const [useWorkbenchAnswersOnly, setUseWorkbenchAnswersOnly] = useState(false);
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('');

  const apiClientId = process.env.REACT_APP_5W_CLIENT_ID || '';
  const apiClientSecret = process.env.REACT_APP_5W_CLIENT_SECRET || '';
  const hasFiveWClientHeaders = Boolean(apiClientId && apiClientSecret);

  const getFiveWHeaders = () => {
    const headers = { 'Content-Type': 'application/json' };
    if (apiClientId) {
      headers['x-client-id'] = apiClientId;
    }
    if (apiClientSecret) {
      headers['x-client-secret'] = apiClientSecret;
    }
    return headers;
  };

  const normalizeApiQuestionResult = (raw) => {
    const candidate = Array.isArray(raw)
      ? raw
      : Array.isArray(raw?.questions)
        ? raw.questions
        : Array.isArray(raw?.result)
          ? raw.result
          : [];

    return candidate
      .map((item, index) => {
        const text = String(item?.questionText || item?.text || item?.question || '').trim();
        const helpText = String(item?.HelpText || item?.helpText || item?.help_text || '').trim();
        if (!text) {
          return null;
        }
        return { text, helpText: helpText || `Guidance ${index + 1}` };
      })
      .filter(Boolean);
  };

  const upsertCategoryQuestions = (categoryKey, nextQuestions) => {
    setCategories((prev) => {
      const existingIndex = prev.findIndex((c) => c.key === categoryKey);
      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex] = {
          ...updated[existingIndex],
          questions: nextQuestions,
        };
        return updated;
      }

      return [...prev, { key: categoryKey, questions: nextQuestions }];
    });
  };

  useEffect(() => {
    const controller = new AbortController();
    const eventid = parseInt(workshopId, 10) || 0;
    const scenarioid = Number(caseStudy?.id) || 0;
    setCategories([]);
    setActiveCategory(null);
    setAnswersByKey({});
    setExistingByKey({});
    setFetchedByCategory({});
    setThreadIdByCategory({});
    setUseWorkbenchAnswersOnly(false);
    setMessage('');
    setStatus('loading');

    if (!eventid || !scenarioid) {
      setStatus('empty');
      setMessage('Create or select a case study to start Prompt Labs.');
      return;
    }

    const scenarioSeed = {
      title: caseStudy?.title || 'Subscription Onboarding Optimization',
      business_problem:
        caseStudy?.business_problem || 'Trial users are not converting to paid plans',
      personas: caseStudy?.personas || 'New user, Team manager, Admin',
      rules: caseStudy?.rules || 'No pricing changes, no major UI rewrite',
      acceptance_criteria:
        caseStudy?.acceptance_criteria || 'Improve trial-to-paid conversion by 15%',
    };

    const initialAnswersPromise = attendeeid
      ? fetch(
          `${BASE_URL}/workshops/workbench/5wanswers?eventid=${eventid}&scenarioid=${scenarioid}`,
          { headers: getWorkshopHeaders(), signal: controller.signal }
        ).then(async (res) => {
          const payload = await res.json().catch(() => ({}));
          if (!res.ok) throw new Error(payload.error || `Failed to load answers (${res.status})`);
          return payload;
        })
      : Promise.resolve({ data: [] });

    initialAnswersPromise
      .then((answersPayload) => {
        const answerRows = Array.isArray(answersPayload?.data) ? answersPayload.data : [];
        const hasWorkbenchAnswers = answerRows.length > 0;

        // Build answer lookup maps keyed by "category|||question"
        const existingMap = {};
        const answerMap = {};
        answerRows.forEach((row) => {
          const category = canonicalize5WCategory(row['5w_category']);
          const question = String(row.question || '').trim();
          if (!category || !question) return;

          const key = `${category}|||${question}`;
          existingMap[key] = row;
          answerMap[key] = row.answer || '';
        });

        setExistingByKey(existingMap);
        setAnswersByKey(answerMap);

        // Reconstruct question structure from saved answer rows (grouped by category)
        const STANDARD_ORDER = ['Why', 'What', 'Who', 'When', 'Where'];
        const categoryMap = {};
        answerRows.forEach((row) => {
          const cat = canonicalize5WCategory(row['5w_category']);
          const question = String(row.question || '').trim();
          if (!cat || !question) return;

          if (!categoryMap[cat]) categoryMap[cat] = new Map();
          if (!categoryMap[cat].has(question)) {
            categoryMap[cat].set(question, { text: question, helpText: '' });
          }
        });

        const questionSource = SAMPLE_5W_QUESTION_BANK;

        // Always keep all standard 5W tabs visible. When answers exist for a category,
        // merge them and preserve saved responses; for missing categories, keep sample seed.
        questionSource.forEach((cat) => {
          if (!categoryMap[cat.key]) {
            categoryMap[cat.key] = new Map();
          }
          cat.questions.forEach((q) => {
            if (!categoryMap[cat.key].has(q.text)) {
              categoryMap[cat.key].set(q.text, { text: q.text, helpText: q.helpText || '' });
            }
          });
        });

        // Sort categories in standard 5W order, then any extras
        const sortedKeys = [
          ...STANDARD_ORDER.filter((k) => categoryMap[k]),
          ...Object.keys(categoryMap).filter((k) => !STANDARD_ORDER.includes(k)),
        ];

        const resolvedCategories = sortedKeys.map((key) => ({
          key,
          questions: Array.from(categoryMap[key].values()),
        }));

        setCategories(resolvedCategories);
        setActiveCategory((prev) => {
          const normalizedPrev = canonicalize5WCategory(prev);
          if (normalizedPrev && resolvedCategories.some((c) => c.key === normalizedPrev)) {
            return normalizedPrev;
          }
          return resolvedCategories[0]?.key || null;
        });
        setFetchedByCategory({});
        setThreadIdByCategory({});
        setUseWorkbenchAnswersOnly(hasWorkbenchAnswers);
        setStatus('ready');

        if (hasWorkbenchAnswers) {
          setMessage('Loaded saved 5W answers from your workbench.');
        } else if (!hasFiveWClientHeaders) {
          setMessage('5W API credentials are not configured in env. Showing sample 5W questions.');
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        setCategories(SAMPLE_5W_QUESTION_BANK);
        setActiveCategory((prev) => canonicalize5WCategory(prev) || SAMPLE_5W_QUESTION_BANK[0]?.key || null);
        setStatus('error');
        setMessage(error.message || 'Saved answers could not be loaded.');
      });
    return () => controller.abort();
  }, [workshopId, attendeeid, caseStudy, hasFiveWClientHeaders]);

  useEffect(() => {
    const controller = new AbortController();
    if (status !== 'ready' || !activeCategory) {
      return;
    }

    if (useWorkbenchAnswersOnly) {
      return;
    }

    if (!hasFiveWClientHeaders) {
      return;
    }

    const normalizedCategory = String(activeCategory || '').trim().toUpperCase();
    if (!normalizedCategory || fetchedByCategory[normalizedCategory]) {
      return;
    }

    const scenarioPayload = {
      title: caseStudy?.title || 'Subscription Onboarding Optimization',
      business_problem:
        caseStudy?.business_problem || 'Trial users are not converting to paid plans',
      personas: caseStudy?.personas || 'New user, Team manager, Admin',
      rules: caseStudy?.rules || 'No pricing changes, no major UI rewrite',
      acceptance_criteria:
        caseStudy?.acceptance_criteria || 'Improve trial-to-paid conversion by 15%',
    };

    fetch(`${BASE_URL}/5w/questions`, {
      method: 'POST',
      headers: getFiveWHeaders(),
      signal: controller.signal,
      body: JSON.stringify({
        userrole: userRole,
        '5wcategory': normalizedCategory,
        focusarea: '',
        casestudy: scenarioPayload,
      }),
    })
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error(`5W API failed (${res.status})`))) )
      .then((payload) => {
        const normalized = normalizeApiQuestionResult(payload?.data?.result);
        if (normalized.length > 0) {
          upsertCategoryQuestions(activeCategory, normalized);
          setThreadIdByCategory((prev) => ({
            ...prev,
            [normalizedCategory]: payload?.data?.threadId || payload?.data?.result?.threadId || null,
          }));
        }
        setFetchedByCategory((prev) => ({ ...prev, [normalizedCategory]: true }));
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        setFetchedByCategory((prev) => ({ ...prev, [normalizedCategory]: true }));
      });
    return () => controller.abort();
  }, [activeCategory, status, fetchedByCategory, userRole, caseStudy, hasFiveWClientHeaders, useWorkbenchAnswersOnly]);

  const tabs = useMemo(() => {
    const deduped = [];
    categories.forEach((category) => {
      const key = canonicalize5WCategory(category?.key);
      if (key && !deduped.includes(key)) {
        deduped.push(key);
      }
    });

    return deduped.length > 0 ? deduped : SAMPLE_5W_QUESTION_BANK.map((c) => c.key);
  }, [categories]);

  const activeQuestions = useMemo(() => {
    const normalizedActiveCategory = canonicalize5WCategory(activeCategory);
    const selectedCategory = categories.find(
      (c) => canonicalize5WCategory(c.key) === normalizedActiveCategory
    );
    return Array.isArray(selectedCategory?.questions) ? selectedCategory.questions : [];
  }, [categories, activeCategory]);

  const setAnswer = (questionText, value) => {
    const key = `${activeCategory}|||${questionText}`;
    setAnswersByKey((prev) => ({ ...prev, [key]: value }));
  };

  const activeCategoryExportText = useMemo(() => {
    const lines = [`Category: ${activeCategory}`, ''];

    activeQuestions.forEach((question, idx) => {
      const key = `${activeCategory}|||${question.text}`;
      const answer = answersByKey[key] || '';
      lines.push(`${idx + 1}. ${question.text}`);
      if (question.helpText) {
        lines.push(`Help: ${question.helpText}`);
      }
      lines.push(`Answer: ${answer}`);
      lines.push('');
    });

    return lines.join('\n');
  }, [activeCategory, activeQuestions, answersByKey]);

  const copyPromptLabsContent = async () => {
    try {
      await navigator.clipboard.writeText(activeCategoryExportText);
      setMessage(`Copied ${activeCategory} content.`);
    } catch (_error) {
      setMessage('Copy failed. Please copy manually.');
    }
  };

  const downloadPromptLabsContent = () => {
    const fileCategory = String(activeCategory || 'category').toLowerCase();
    downloadTextFile(`5w-${fileCategory}.txt`, activeCategoryExportText);
    setMessage(`Downloaded ${activeCategory} content.`);
  };

  const saveCategoryAnswers = async () => {
    if (!attendeeid) {
      setStatus('error');
      setMessage('Attendee not found. Please login again.');
      return;
    }

    setStatus('saving');
    setMessage('');

    try {
      const eventid = parseInt(workshopId, 10) || 0;
      const scenarioid = Number(caseStudy?.id) || 0;
      if (!eventid || !scenarioid) {
        throw new Error('Create or select a case study before saving answers.');
      }
      for (const question of activeQuestions) {
        const questionText = question.text;
        const key = `${activeCategory}|||${questionText}`;
        const answer = answersByKey[key] || '';
        const createdon = new Date().toISOString();

        const body = {
          eventid,
          scenarioid,
          '5w_category': activeCategory,
          question: questionText,
          answer,
          createdon,
        };

        const existing = existingByKey[key];
        const endpoint = existing?.id
          ? `${BASE_URL}/workshops/workbench/5wanswers/${existing.id}`
          : `${BASE_URL}/workshops/workbench/5wanswers`;
        const res = await fetch(endpoint, {
          method: existing?.id ? 'PUT' : 'POST',
          headers: getWorkshopHeaders(true),
          body: JSON.stringify(body),
        });

        const payload = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(payload.error || payload.message || `Failed to save answer (${res.status})`);

        const saved = payload?.data;
        if (saved?.id) {
          setExistingByKey((prev) => ({ ...prev, [key]: saved }));
        }
      }

      setStatus('saved');
      setMessage(`${activeCategory} answers saved successfully.`);
      onLessonComplete?.();
      setTimeout(() => setStatus('ready'), 1200);
    } catch (error) {
      setStatus('error');
      setMessage(error.message || 'Failed to save answers.');
    }
  };

  const handleRoleChange = (e) => {
    onRoleChange(e.target.value);
    // Clear fetch cache so the active tab re-fetches with the new role
    setFetchedByCategory({});
  };

  return (
    <div>
      <div className="wk-5w-role-row">
        <label className="wk-5w-role-label" htmlFor="wk-5w-role-select">Your SDLC Role</label>
        <select
          id="wk-5w-role-select"
          className="wk-form-input wk-5w-role-select"
          value={userRole}
          onChange={handleRoleChange}
        >
          <option value="">— Select your role —</option>
          {SDLC_ROLES.map((role) => (
            <option key={role} value={role}>{role}</option>
          ))}
        </select>
      </div>

      <div className="wk-5w-tabs" role="tablist" aria-label="5W categories">
        {tabs.map((tab) => (
          <button
            key={tab}
            type="button"
            role="tab"
            aria-selected={tab === activeCategory}
            className={`wk-5w-tab${tab === activeCategory ? ' wk-5w-tab-active' : ''}`}
            onClick={() => setActiveCategory(canonicalize5WCategory(tab))}
          >
            {tab}
          </button>
        ))}
      </div>

      <div className="wk-form-footer" style={{ justifyContent: 'flex-end' }}>
        <IconActionButton label="" onClick={copyPromptLabsContent}>
          <CopyIcon />
        </IconActionButton>
        <IconActionButton label="" onClick={downloadPromptLabsContent}>
          <DownloadIcon />
        </IconActionButton>
      </div>

      {status === 'loading' ? <p className="wk-form-loading">Loading 5W questions…</p> : null}
      {threadIdByCategory[String(activeCategory || '').trim().toUpperCase()] ? (
        <p className="wk-form-help">
        </p>
      ) : null}
      {activeQuestions.length === 0 && status !== 'loading' ? <p className="wk-form-loading">No questions configured for this tab.</p> : null}

      {activeQuestions.map((question, idx) => {
        const key = `${activeCategory}|||${question.text}`;
        return (
          <div key={key} className="wk-survey-question">
            <p className="wk-survey-q-text">
              <span>{idx + 1}. {question.text}</span>
            </p>
            {question.helpText ? <p className="wk-form-help">{question.helpText}</p> : null}
            <textarea
              className="wk-workarea-input"
              rows={4}
              placeholder="Write your response..."
              value={answersByKey[key] || ''}
              onChange={(e) => setAnswer(question.text, e.target.value)}
            />
          </div>
        );
      })}

      <div className="wk-form-footer">
        <p className="wk-form-footnote">Answers are saved per tab and can be updated anytime.</p>
        <button
          type="button"
          className="ep-btn-primary wk-form-submit"
          onClick={saveCategoryAnswers}
          disabled={status === 'saving' || status === 'loading'}
        >
          {status === 'saving' ? 'Saving…' : `Save`}
        </button>
      </div>

      {message ? <p className="wk-form-success">{message}</p> : null}
    </div>
  );
};

const ModuleInsightPanel = ({ blocks, status }) => {
  return (
    <section className="wk-content-card">
      
       
       
       
    </section>
  );
};

/**
 * Renders a single value as markdown lines.
 * - string/number/boolean  → paragraph text
 * - array of primitives    → bullet list
 * - array of objects       → sub-bullet JSON (readable)
 * - plain object           → bold key: value pairs
 */
const renderValue = (value, lines) => {
  if (Array.isArray(value)) {
    value.forEach((item) => {
      if (item && typeof item === 'object') {
        Object.entries(item).forEach(([k, v]) => lines.push(`  - **${k}:** ${v}`));
      } else {
        lines.push(`- ${item}`);
      }
    });
  } else if (value && typeof value === 'object') {
    Object.entries(value).forEach(([k, v]) => {
      if (Array.isArray(v)) {
        lines.push(`**${k}:**`);
        v.forEach((item) =>
          lines.push(typeof item === 'object' ? `  - ${JSON.stringify(item)}` : `  - ${item}`)
        );
      } else {
        lines.push(`**${k}:** ${v}`);
      }
    });
  } else if (value !== undefined && value !== null && value !== '') {
    lines.push(String(value));
  }
};

const tryParseJsonText = (text) => {
  if (typeof text !== 'string') return null;

  const raw = text.trim();
  if (!raw) return null;

  const candidates = [];
  candidates.push(raw);

  const fencedJsonMatch = raw.match(/^```(?:json)?\s*([\s\S]*?)\s*```$/i);
  if (fencedJsonMatch && fencedJsonMatch[1]) {
    candidates.push(fencedJsonMatch[1].trim());
  }

  const jsonBlockMatch = raw.match(/(\{[\s\S]*\}|\[[\s\S]*\])/);
  if (jsonBlockMatch && jsonBlockMatch[1]) {
    candidates.push(jsonBlockMatch[1].trim());
  }

  for (const candidate of candidates) {
    try {
      const parsed = JSON.parse(candidate);
      if (parsed && typeof parsed === 'object') {
        return parsed;
      }
    } catch (_error) {
      // Try next candidate.
    }
  }

  return null;
};

const pickFirstNonEmptyObject = (candidates) => {
  for (const item of candidates) {
    if (item && typeof item === 'object' && !Array.isArray(item) && Object.keys(item).length > 0) {
      return item;
    }
  }
  return null;
};

const resolveArtifactDocumentByTab = (artifact, tab) => {
  const normalizeCandidate = (candidate) => {
    if (!candidate) return null;
    if (typeof candidate === 'string') return tryParseJsonText(candidate);
    if (typeof candidate === 'object' && typeof candidate.rawText === 'string') {
      return tryParseJsonText(candidate.rawText) || candidate;
    }
    return candidate;
  };

  let root = normalizeCandidate(artifact);
  if (!root || typeof root !== 'object') return null;

  // Some agents return nested wrappers.
  if (root.artifact) {
    const nested = normalizeCandidate(root.artifact);
    if (nested && typeof nested === 'object') {
      root = nested;
    }
  }

  const tabKeyMap = {
    Context: ['AIContextDocument', 'context', 'Context'],
    Prompt: ['AIPromptDocument', 'prompt', 'Prompt'],
    'Agent Instructions': [
      'AIAgentInstructions',
      'AIAgentInstructionsDocument',
      'agentInstructions',
      'AgentInstructions',
    ],
  };

  const mappedKeys = tabKeyMap[tab] || [];
  const mappedCandidates = mappedKeys.map((k) => normalizeCandidate(root[k]));
  const mappedDoc = pickFirstNonEmptyObject(mappedCandidates);
  if (mappedDoc) return mappedDoc;

  return normalizeCandidate(root);
};

/**
 * Converts any AI artifact JSON (AIContextDocument, AIPromptDocument,
 * AIAgentInstructions, or any generic shape) into a merged markdown string
 * so the textarea never displays raw JSON.
 *
 * Resolution order for the root document:
 *   1. artifact.AIContextDocument
 *   2. artifact.AIPromptDocument
 *   3. artifact.AIAgentInstructions  (or AIAgentInstructionsDocument)
 *   4. artifact itself (generic object)
 */
const formatArtifactAsMarkdown = (artifact) => {
  const resolvedArtifact =
    typeof artifact === 'string' ? tryParseJsonText(artifact) || artifact : artifact;

  if (typeof resolvedArtifact === 'string') return resolvedArtifact;
  if (!resolvedArtifact || typeof resolvedArtifact !== 'object') return '';

  const doc =
    resolvedArtifact.AIContextDocument ||
    resolvedArtifact.AIPromptDocument ||
    resolvedArtifact.AIAgentInstructions ||
    resolvedArtifact.AIAgentInstructionsDocument ||
    resolvedArtifact;

  const lines = [];

  // Always render Title as H1 first
  if (doc.Title) {
    lines.push(`# ${doc.Title}`, '');
  }

  // Render every remaining key generically — preserves all sections for all
  // artifact types (Context, Prompt, Agent Instructions) without hard-coding.
  Object.entries(doc).forEach(([key, value]) => {
    if (key === 'Title' || key === 'rawText') return; // already handled or not user-friendly
    lines.push(`## ${key}`, '');
    renderValue(value, lines);
    lines.push('');
  });

  return lines.join('\n').trim();
};

const AI_ARTIFACT_TABS = ['Context', 'Prompt', 'Agent Instructions'];

const AI_ARTIFACT_DEFAULT_CONTENT = {
  Context:
    'Capture project background, goals, constraints, and relevant references. This section will be API-powered later.',
  Prompt:
    'Draft and iterate your working prompt templates here. Prompt versioning and retrieval will be wired to API later.',
  'Agent Instructions':
    'Define role, boundaries, output format, and guardrails for your agent. Instruction persistence will be added with API integration.',
};

const TAB_ASK_MAP = {
  Context: 'context',
  Prompt: 'prompt',
  'Agent Instructions': 'agent-instructions',
};

const AIArtifactsInline = ({ userRole, caseStudy, workshopId, onLessonComplete }) => {
  const [activeTab, setActiveTab] = useState(null);
  const [artifactContentByTab, setArtifactContentByTab] = useState({});
  const [artifactStatus, setArtifactStatus] = useState('idle'); // idle | loading | error
  const [fetchedTabs, setFetchedTabs] = useState({});
  const [artifactMessage, setArtifactMessage] = useState('');
  const requestVersionRef = useRef(0);

  const apiClientId = process.env.REACT_APP_5W_CLIENT_ID || '';
  const apiClientSecret = process.env.REACT_APP_5W_CLIENT_SECRET || '';
  const hasArtifactHeaders = Boolean(apiClientId && apiClientSecret);
  const attendeeid = getLoggedInAttendeeId();

  useEffect(() => {
    requestVersionRef.current += 1;
    setActiveTab(null);
    setArtifactContentByTab({});
    setFetchedTabs({});
    setArtifactStatus('idle');
    setArtifactMessage('');
  }, [caseStudy?.id]);

  const getArtifactHeaders = () => {
    const headers = { 'Content-Type': 'application/json' };
    if (apiClientId) headers['x-client-id'] = apiClientId;
    if (apiClientSecret) headers['x-client-secret'] = apiClientSecret;
    return headers;
  };

  const buildAnswersPayload = async () => {
    const eventid = parseInt(workshopId, 10) || 0;
    const scenarioid = Number(caseStudy?.id) || 0;
    if (!attendeeid || !eventid || !scenarioid) return {};
    const res = await fetch(
      `${BASE_URL}/workshops/workbench/5wanswers?eventid=${eventid}&scenarioid=${scenarioid}`,
      { headers: getWorkshopHeaders() }
    );
    const payload = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(payload.error || `Failed to load artifact answers (${res.status})`);
    const rows = Array.isArray(payload?.data) ? payload.data : [];
    const grouped = {};
    rows.forEach((row) => {
      const cat = row['5w_category'];
      if (!cat) return;
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat].push({ question: row.question || '', answer: row.answer || '' });
    });
    return grouped;
  };

  const fetchArtifact = async (tab) => {
    const requestVersion = ++requestVersionRef.current;
    setArtifactStatus('loading');
    setArtifactMessage('');

    try {
      if (!caseStudy?.id) {
        throw new Error('Create or select a case study before generating artifacts.');
      }
      const answers = await buildAnswersPayload();
      const ask = TAB_ASK_MAP[tab] || 'context';

      const res = await fetch(`${BASE_URL}/5w/artifacts`, {
        method: 'POST',
        headers: getArtifactHeaders(),
        body: JSON.stringify({
          userrole: userRole || '',
          ask,
          casestudy: {
            title: caseStudy?.title || '',
            business_problem: caseStudy?.business_problem || '',
            personas: caseStudy?.personas || '',
            rules: caseStudy?.rules || '',
            acceptance_criteria: caseStudy?.acceptance_criteria || '',
          },
          answers,
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (res.status === 401 || res.status === 403) {
          const authHint = hasArtifactHeaders
            ? 'Client credentials are invalid for the 5W API.'
            : 'Client credentials are missing. Set REACT_APP_5W_CLIENT_ID and REACT_APP_5W_CLIENT_SECRET in the frontend environment.';
          throw new Error(data?.message || authHint);
        }
        throw new Error(data?.message || `Request failed (${res.status})`);
      }

      const artifact = data?.data?.artifact;
      // If the API returns artifact as a JSON-encoded string, parse it first
      // so the markdown formatter always receives an object when applicable.
      const resolvedArtifact = (() => {
        if (typeof artifact !== 'string') return artifact;
        try {
          const parsed = JSON.parse(artifact);
          return parsed && typeof parsed === 'object' ? parsed : artifact;
        } catch (_) {
          return artifact;
        }
      })();
      const tabDocument = resolveArtifactDocumentByTab(resolvedArtifact, tab);
      const artifactText = tabDocument ? formatArtifactAsMarkdown(tabDocument) : '';

      if (requestVersion !== requestVersionRef.current) return;
      setArtifactContentByTab((prev) => ({ ...prev, [tab]: artifactText }));
      setFetchedTabs((prev) => ({ ...prev, [tab]: true }));
      setArtifactStatus('idle');
    } catch (err) {
      if (requestVersion !== requestVersionRef.current) return;
      setArtifactStatus('error');
      setArtifactMessage(err.message || 'Failed to generate artifact.');
    }
  };

  const handleTabClick = (tab) => {
    setActiveTab(tab);
    onLessonComplete?.();
    if (!fetchedTabs[tab]) {
      fetchArtifact(tab);
    }
  };

  const tabBody = activeTab
    ? (artifactContentByTab[activeTab] ?? AI_ARTIFACT_DEFAULT_CONTENT[activeTab] ?? '')
    : '';

  const onArtifactContentChange = (value) => {
    setArtifactContentByTab((prev) => ({ ...prev, [activeTab]: value }));
  };

  const copyArtifactContent = async () => {
    try {
      await navigator.clipboard.writeText(tabBody);
      setArtifactMessage(`Copied ${activeTab}.`);
    } catch (_error) {
      setArtifactMessage('Copy failed. Please copy manually.');
    }
  };

  const downloadArtifactContent = () => {
    const safeTabName = String(activeTab || 'artifact').toLowerCase().replace(/[^a-z0-9]+/g, '-');
    downloadTextFile(`ai-artifact-${safeTabName}.txt`, tabBody);
    setArtifactMessage(`Downloaded ${activeTab}.`);
  };

  return (
    <section className="wk-content-card">
      <h3>AI Artifacts</h3>
      <p>
        Select a tab to generate AI-powered context, prompt templates, or agent instructions
        based on your 5W answers and case study.
      </p>

      <div className="wk-5w-tabs" role="tablist" aria-label="AI Artifacts tabs">
        {AI_ARTIFACT_TABS.map((tab) => (
          <button
            key={tab}
            type="button"
            role="tab"
            aria-selected={tab === activeTab}
            className={`wk-5w-tab${tab === activeTab ? ' wk-5w-tab-active' : ''}`}
            onClick={() => handleTabClick(tab)}
          >
            {tab}
          </button>
        ))}
      </div>

      {activeTab ? (
        <>
          <div className="wk-form-footer" style={{ justifyContent: 'flex-end' }}>
            <IconActionButton label="" onClick={copyArtifactContent}>
              <CopyIcon />
            </IconActionButton>
            <IconActionButton label="" onClick={downloadArtifactContent}>
              <DownloadIcon />
            </IconActionButton>
          </div>

          {artifactStatus === 'loading' ? (
            <p className="wk-form-loading" role="status">Generating {activeTab}…</p>
          ) : null}

          <div className="wk-survey-question">
            <p className="wk-survey-q-text"><span>{activeTab}</span></p>
            <textarea
              className="wk-workarea-input"
              rows={10}
              value={tabBody}
              placeholder={artifactStatus === 'loading' ? 'Generating…' : `${activeTab} will appear here after generation.`}
              onChange={(e) => onArtifactContentChange(e.target.value)}
              disabled={artifactStatus === 'loading'}
            />
          </div>

          {artifactMessage ? (
            <p
              className={`wk-form-${artifactStatus === 'error' ? 'error' : 'success'}`}
              role="status"
            >
              {artifactMessage}
            </p>
          ) : null}
        </>
      ) : (
        <p className="wk-form-help" style={{ marginTop: 8 }}>
          Click a tab above to generate your artifact.
        </p>
      )}
    </section>
  );
};

const CaseStudyForm = ({ workshopId, caseStudy, onSaved }) => {
  const [form, setForm] = useState(CASE_STUDY_INITIAL);
  const [status, setStatus] = useState(null); // null | 'loading' | 'submitting' | 'success' | 'error'
  const [errorMsg, setErrorMsg] = useState('');
  const [isLibraryOpen, setIsLibraryOpen] = useState(false);
  const [librarySearch, setLibrarySearch] = useState('');
  const [selectedLibraryCaseId, setSelectedLibraryCaseId] = useState('');
  const attendeeid = getLoggedInAttendeeId();
  const totalSections = CASE_STUDY_FIELDS.length;
  const caseStudyLibrary = useMemo(
    () => normalizeCaseStudyLibrary(caseStudiesLibraryData),
    []
  );

  const filteredCaseStudyLibrary = useMemo(() => {
    const query = librarySearch.trim().toLowerCase();
    if (!query) return caseStudyLibrary;

    return caseStudyLibrary.filter((item) => {
      const searchableText = [
        item.title,
        item.description,
        item.domain,
        item.difficulty,
        item.business_problem,
        item.personas,
        item.rules,
        item.acceptance_criteria,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return searchableText.includes(query);
    });
  }, [caseStudyLibrary, librarySearch]);

  const selectedLibraryCase = useMemo(
    () => caseStudyLibrary.find((item) => item.id === selectedLibraryCaseId) || null,
    [caseStudyLibrary, selectedLibraryCaseId]
  );

  const completedSections = useMemo(
    () => Object.values(form).filter((value) => String(value || '').trim().length > 0).length,
    [form]
  );

  useEffect(() => {
    setForm(caseStudy ? {
      title: caseStudy.title || '',
      description: caseStudy.description || '',
      business_problem: caseStudy.business_problem || '',
      personas: caseStudy.personas || '',
      rules: caseStudy.rules || '',
      acceptance_criteria: caseStudy.acceptance_criteria || '',
    } : CASE_STUDY_INITIAL);
    setStatus(null);
    setErrorMsg('');
    setSelectedLibraryCaseId('');
  }, [caseStudy]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const applyLibraryCaseStudy = (caseStudy) => {
    if (!caseStudy) return;

    setForm({
      title: caseStudy.title || '',
      description: caseStudy.description || '',
      business_problem: caseStudy.business_problem || '',
      personas: caseStudy.personas || '',
      rules: caseStudy.rules || '',
      acceptance_criteria: caseStudy.acceptance_criteria || '',
    });

    setSelectedLibraryCaseId(caseStudy.id);
    setIsLibraryOpen(false);
    setLibrarySearch('');
    setStatus(null);
    setErrorMsg('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('submitting');
    setErrorMsg('');

    const eventid = parseInt(workshopId, 10) || 0;
    if (!attendeeid) {
      setStatus('error');
      setErrorMsg('Logged-in user not found. Please sign in again.');
      return;
    }

    try {
      const isEditing = Boolean(caseStudy?.id);
      const res = await fetch(
        isEditing
          ? `${BASE_URL}/workshops/workbench/case-studies/${caseStudy.id}`
          : `${BASE_URL}/workshops/workbench/case-studies`,
        {
        method: isEditing ? 'PUT' : 'POST',
        headers: getWorkshopHeaders(true),
        body: JSON.stringify({ eventid, ...form }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || `Request failed (${res.status})`);
      }
      const payload = await res.json();
      setStatus('success');
      onSaved?.(payload?.data || null);
    } catch (err) {
      setStatus('error');
      setErrorMsg(err.message);
    }
  };

  return (
    <form className="wk-casestudy-form" onSubmit={handleSubmit} noValidate>
      <div className="wk-cs-library-row">
        <div>
          <p className="wk-cs-library-title">Case Study Library</p>
          <p className="wk-cs-library-subtitle">Choose a prepared case study and auto-fill this form.</p>
        </div>
        <button
          type="button"
          className="ep-btn-ghost wk-cs-library-btn"
          onClick={() => setIsLibraryOpen(true)}
          disabled={caseStudyLibrary.length === 0}
        >
          Choose Case Study
        </button>
      </div>

      {selectedLibraryCase ? (
        <p className="wk-form-success" role="status">
          Loaded from library: {selectedLibraryCase.title}
        </p>
      ) : null}

      <div
        className={`wk-cs-library-overlay${isLibraryOpen ? ' wk-cs-library-overlay-open' : ''}`}
        onClick={() => setIsLibraryOpen(false)}
        aria-hidden={!isLibraryOpen}
      />

      <aside
        className={`wk-cs-library-drawer${isLibraryOpen ? ' wk-cs-library-drawer-open' : ''}`}
        aria-hidden={!isLibraryOpen}
      >
        <div className="wk-cs-drawer-header">
          <div>
            <h4>Case Study Library</h4>
            <p>Browse all options and apply one to the form.</p>
          </div>
          <button
            type="button"
            className="wk-cs-drawer-close"
            onClick={() => setIsLibraryOpen(false)}
            aria-label="Close case study library"
          >
            x
          </button>
        </div>

        {caseStudyLibrary.length === 0 ? (
          <p className="wk-form-help">No case studies available in the library.</p>
        ) : (
          <>
            <div className="wk-cs-library-search-row">
              <input
                type="text"
                className="wk-form-input"
                placeholder="Search case studies by title, domain, difficulty, or content"
                value={librarySearch}
                onChange={(e) => setLibrarySearch(e.target.value)}
                aria-label="Search case studies"
              />
            </div>

            <div className="wk-cs-library-results-row">
              <span className="wk-cs-library-results-count">
                Showing {filteredCaseStudyLibrary.length} of {caseStudyLibrary.length}
              </span>
              {librarySearch ? (
                <button
                  type="button"
                  className="ep-btn-ghost ep-btn-sm"
                  onClick={() => setLibrarySearch('')}
                >
                  Clear Search
                </button>
              ) : null}
            </div>

            <div className="wk-cs-tile-grid" aria-label="Case study library options">
              {filteredCaseStudyLibrary.map((item) => (
                <article
                  key={item.id}
                  className={`wk-cs-tile${selectedLibraryCaseId === item.id ? ' wk-cs-tile-selected' : ''}`}
                >
                  <div className="wk-cs-slide-head">
                    <h5>{item.title}</h5>
                    <div className="wk-cs-slide-meta">
                      {item.domain ? <span>{item.domain}</span> : null}
                      {item.difficulty ? <span>{item.difficulty}</span> : null}
                    </div>
                  </div>
                  <p>{item.description}</p>
                  <ul>
                    <li><strong>Business Problem:</strong> {item.business_problem}</li>
                    <li><strong>Personas:</strong> {item.personas}</li>
                    <li><strong>Rules:</strong> {item.rules}</li>
                    <li><strong>Acceptance:</strong> {item.acceptance_criteria}</li>
                  </ul>
                  <button
                    type="button"
                    className="ep-btn-primary ep-btn-sm"
                    onClick={() => applyLibraryCaseStudy(item)}
                  >
                    Use This Case Study
                  </button>
                </article>
              ))}
            </div>

            {filteredCaseStudyLibrary.length === 0 ? (
              <p className="wk-form-help">No case studies match your search.</p>
            ) : null}
          </>
        )}
      </aside>

      <div className="wk-casestudy-grid">
        {CASE_STUDY_FIELDS.map((field) => (
          <div
            key={field.key}
            className={`wk-form-group ${field.wide ? 'wk-form-group-wide' : ''}`.trim()}
          >
            <label className="wk-form-label" htmlFor={`cs-${field.key}`}>
              {field.label}
              {field.required ? <span className="wk-form-required">Required</span> : null}
            </label>

            {field.type === 'input' ? (
              <input
                id={`cs-${field.key}`}
                name={field.key}
                type="text"
                className="wk-form-input"
                placeholder={field.placeholder}
                value={form[field.key]}
                onChange={handleChange}
                required={field.required}
              />
            ) : (
              <textarea
                id={`cs-${field.key}`}
                name={field.key}
                className="wk-workarea-input"
                placeholder={field.placeholder}
                rows={field.rows || 3}
                value={form[field.key]}
                onChange={handleChange}
              />
            )}

            <p className="wk-form-help">{field.help}</p>
          </div>
        ))}
      </div>

      {status === 'loading' && (
        <p className="wk-form-loading" role="status">Loading saved answers…</p>
      )}
      {status === 'error' && (
        <p className="wk-form-error" role="alert">{errorMsg || 'Submission failed. Please try again.'}</p>
      )}
      {status === 'success' && (
        <p className="wk-form-success" role="status">Case study saved successfully.</p>
      )}

      <div className="wk-form-footer">
        <p className="wk-form-footnote">
          Keep iterating your brief. You can update and save this as you refine your solution.
        </p>
        <button
          type="submit"
          className="ep-btn-primary wk-form-submit"
          disabled={status === 'submitting' || status === 'loading'}
        >
          {status === 'submitting' ? 'Saving…' : caseStudy?.id ? 'Update Case Study' : 'Create Case Study'}
        </button>
      </div>
    </form>
  );
};

const QUESTION_TYPE_LABEL = {
  text:             'Short answer',
  textarea:         'Long answer',
  multiple_choice:  'Single choice',
  checkbox:         'Multiple choice',
  dropdown:         'Dropdown',
  rating:           'Rating',
  yes_no:           'Yes / No',
  date:             'Date',
};

const normalizeQuestionType = (rawType) => {
  const value = String(rawType || '').trim().toLowerCase().replace(/[-\s/]+/g, '_');
  if (value === 'radio' || value === 'single_choice') return 'multiple_choice';
  if (value === 'yesno') return 'yes_no';
  if (value === 'long_text') return 'textarea';
  if (value === 'short_text') return 'text';
  return value;
};

const normalizeSurveyPayload = (payload) => {
  const rawSurvey = payload?.data || null;
  if (!rawSurvey) return null;

  const rawQuestions = Array.isArray(rawSurvey.questions)
    ? rawSurvey.questions
    : Array.isArray(rawSurvey.Questions)
      ? rawSurvey.Questions
      : [];

  return {
    ...rawSurvey,
    questions: rawQuestions.map((q) => ({
      ...q,
      question_type: normalizeQuestionType(q.question_type),
      options: Array.isArray(q.options) ? q.options : [],
      display_order: Number.isFinite(q.display_order) ? q.display_order : 0,
      rating_max: Number.isFinite(q.rating_max) ? q.rating_max : parseInt(q.rating_max, 10) || 5,
    })),
  };
};

const SurveyWidget = ({ surveyConfig, onSubmitted }) => {
  const [survey, setSurvey]             = useState(null);
  const [surveyStatus, setSurveyStatus] = useState('loading');
  const [answers, setAnswers]           = useState({});
  const [submitMsg, setSubmitMsg]       = useState('');

  useEffect(() => {
    if (!surveyConfig?.surveyid) { setSurveyStatus('error'); return; }
    fetch(`${BASE_URL}/surveys/${surveyConfig.surveyid}/take`)
      .then((res) => (res.ok ? res.json() : Promise.reject(res.status)))
      .then((payload) => {
        const normalizedSurvey = normalizeSurveyPayload(payload);
        setSurvey(normalizedSurvey);
        setSurveyStatus(normalizedSurvey ? 'ready' : 'error');
      })
      .catch(() => setSurveyStatus('error'));
  }, [surveyConfig?.surveyid]);

  const handleAnswer = (questionId, value) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  const handleCheckbox = (questionId, optionId, checked) => {
    setAnswers((prev) => {
      const current = Array.isArray(prev[questionId]) ? prev[questionId] : [];
      return {
        ...prev,
        [questionId]: checked
          ? [...current, optionId]
          : current.filter((id) => id !== optionId),
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSurveyStatus('submitting');
    const attendeeEmail = localStorage.getItem('attendeeEmail') || localStorage.getItem('userEmail') || '';
    const attendeeName  = localStorage.getItem('attendeeName')  || localStorage.getItem('userName')  || '';

    const builtAnswers = (survey?.questions || []).map((q) => {
      const val = answers[q.id];
      if (q.question_type === 'checkbox') {
        return { question_id: q.id, option_ids: Array.isArray(val) ? val : [] };
      }
      if (['multiple_choice', 'dropdown'].includes(q.question_type)) {
        return { question_id: q.id, option_id: val || null };
      }
      // rating, yes_no, text, textarea, date → answer_text
      return { question_id: q.id, answer_text: val !== undefined && val !== null ? String(val) : '' };
    });

    try {
      const res = await fetch(`${BASE_URL}/surveys/${surveyConfig.surveyid}/respond`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ respondent_email: attendeeEmail, respondent_name: attendeeName, answers: builtAnswers }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || `Request failed (${res.status})`);
      setSubmitMsg(data?.message || 'Thank you for your response!');
      setSurveyStatus('submitted');
      onSubmitted?.();
    } catch (err) {
      setSubmitMsg(err.message);
      setSurveyStatus('error');
    }
  };

  if (surveyStatus === 'loading') {
    return <p className="wk-form-loading" role="status">Loading survey…</p>;
  }
  if (surveyStatus === 'error') {
    return <p className="wk-form-error" role="alert">{submitMsg || 'Survey could not be loaded.'}</p>;
  }
  if (surveyStatus === 'submitted') {
    return <p className="wk-form-success" role="status">{submitMsg}</p>;
  }

  const questions = survey?.questions || [];

  return (
    <form className="wk-survey-form" onSubmit={handleSubmit} noValidate>
      {questions.length === 0 ? (
        <p className="wk-form-loading" role="status">No questions are configured for this survey yet.</p>
      ) : null}

      {questions.map((q, idx) => {
        const options = Array.isArray(q.options) ? q.options : [];
        const ratingMax = q.rating_max || 5;

        return (
          <div key={q.id} className="wk-survey-question">
            <p className="wk-survey-q-text">
              <span>{idx + 1}. {q.question_text}</span>
              {q.is_required ? <span className="wk-form-required">Required</span> : null}
            </p>
            {q.helper_text ? <p className="wk-form-help">{q.helper_text}</p> : null}
            <p className="wk-survey-q-type">{QUESTION_TYPE_LABEL[q.question_type] || q.question_type}</p>

            {q.question_type === 'text' && (
              <input className="wk-form-input" type="text"
                required={q.is_required}
                value={answers[q.id] || ''}
                onChange={(e) => handleAnswer(q.id, e.target.value)} />
            )}

            {q.question_type === 'textarea' && (
              <textarea className="wk-workarea-input" rows={3}
                required={q.is_required}
                value={answers[q.id] || ''}
                onChange={(e) => handleAnswer(q.id, e.target.value)} />
            )}

            {q.question_type === 'rating' && (
              <div className="wk-survey-rating">
                {q.rating_label_low ? <span className="wk-rating-end-label">{q.rating_label_low}</span> : null}
                {Array.from({ length: ratingMax }, (_, i) => i + 1).map((n) => (
                  <button key={n} type="button"
                    className={`wk-rating-btn${String(answers[q.id]) === String(n) ? ' wk-rating-btn-active' : ''}`}
                    onClick={() => handleAnswer(q.id, String(n))}>{n}</button>
                ))}
                {q.rating_label_high ? <span className="wk-rating-end-label">{q.rating_label_high}</span> : null}
              </div>
            )}

            {q.question_type === 'yes_no' && (
              <div className="wk-survey-rating">
                {['Yes', 'No'].map((opt) => (
                  <button key={opt} type="button"
                    className={`wk-rating-btn${answers[q.id] === opt ? ' wk-rating-btn-active' : ''}`}
                    onClick={() => handleAnswer(q.id, opt)}>{opt}</button>
                ))}
              </div>
            )}

            {q.question_type === 'multiple_choice' && (
              <div className="wk-survey-options">
                {options.map((opt) => (
                  <label key={opt.id} className="wk-survey-option">
                    <input type="radio" name={`q-${q.id}`} value={opt.id}
                      checked={answers[q.id] === opt.id}
                      onChange={() => handleAnswer(q.id, opt.id)} />
                    {opt.option_text}
                  </label>
                ))}
              </div>
            )}

            {q.question_type === 'dropdown' && (
              <select className="wk-form-input wk-survey-select"
                required={q.is_required}
                value={answers[q.id] || ''}
                onChange={(e) => handleAnswer(q.id, parseInt(e.target.value, 10))}>
                <option value="">— Select an option —</option>
                {options.map((opt) => (
                  <option key={opt.id} value={opt.id}>{opt.option_text}</option>
                ))}
              </select>
            )}

            {q.question_type === 'checkbox' && (
              <div className="wk-survey-options">
                {options.map((opt) => (
                  <label key={opt.id} className="wk-survey-option">
                    <input type="checkbox" value={opt.id}
                      checked={(Array.isArray(answers[q.id]) ? answers[q.id] : []).includes(opt.id)}
                      onChange={(e) => handleCheckbox(q.id, opt.id, e.target.checked)} />
                    {opt.option_text}
                  </label>
                ))}
              </div>
            )}

            {q.question_type === 'date' && (
              <input className="wk-form-input" type="date"
                required={q.is_required}
                value={answers[q.id] || ''}
                onChange={(e) => handleAnswer(q.id, e.target.value)} />
            )}
          </div>
        );
      })}

      {surveyStatus === 'error' && <p className="wk-form-error" role="alert">{submitMsg}</p>}

      <div className="wk-form-footer">
        <p className="wk-form-footnote">Your response helps tailor the workshop experience.</p>
        <button type="submit" className="ep-btn-primary wk-form-submit"
          disabled={surveyStatus === 'submitting'}>
          {surveyStatus === 'submitting' ? 'Submitting…' : 'Submit Survey'}
        </button>
      </div>
    </form>
  );
};

const PreRequisitesContent = ({ data }) => {
  const {
    overview,
    requiredSoftware = [],
    githubCopilot,
    troubleshooting = [],
    signOffChecklist = [],
  } = data || prerequisitesData;

  return (
    <div className="prereq-content">
      {overview && <p className="prereq-overview">{overview}</p>}

      <section className="prereq-section">
        <h3>Required Software</h3>
        <table className="prereq-table">
          <thead>
            <tr>
              <th>Software</th>
              <th>Purpose</th>
              <th>Download</th>
              <th>Verify</th>
            </tr>
          </thead>
          <tbody>
            {requiredSoftware.map((sw) => (
              <tr key={sw.name}>
                <td><strong>{sw.name}</strong></td>
                <td>{sw.purpose}</td>
                <td>
                  <a href={sw.downloadUrl} target="_blank" rel="noopener noreferrer">
                    Download
                  </a>
                </td>
                <td>
                  {sw.verifyCommands && sw.verifyCommands.length > 0 && (
                    <code className="prereq-cmd">
                      {sw.verifyCommands.join(' / ')}
                    </code>
                  )}
                  {sw.verifyInstruction && (
                    <span>{sw.verifyInstruction}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {githubCopilot && (
        <section className="prereq-section">
          <h3>{githubCopilot.title}</h3>
          <p>{githubCopilot.description}</p>
          <p>
            <a href={githubCopilot.url} target="_blank" rel="noopener noreferrer">
              {githubCopilot.urlLabel}
            </a>
            {' — '}{githubCopilot.instruction}
          </p>
        </section>
      )}

      {troubleshooting.length > 0 && (
        <section className="prereq-section">
          <h3>Troubleshooting</h3>
          <ul className="prereq-list">
            {troubleshooting.map((tip) => (
              <li key={tip}>{tip}</li>
            ))}
          </ul>
        </section>
      )}

      {signOffChecklist.length > 0 && (
        <section className="prereq-section">
          <h3>Sign-off Checklist</h3>
          <ul className="prereq-checklist wk-checklist">
            {signOffChecklist.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
};

const WorkshopWorkArea = ({ workshopTitle, moduleKey, moduleTitle, lesson, workshopId, onLessonComplete }) => {
  const safeLesson = lesson || { key: '', title: 'Lesson', objective: '', checklist: [] };
  const safeChecklist = Array.isArray(safeLesson.checklist) ? safeLesson.checklist : [];
  const isCaseStudy = safeLesson.key === 'case-study';
  const isPromptLabs = safeLesson.key === 'prompt-labs';
  const isAIArtifacts = String(safeLesson.key || '').toLowerCase() === 'ai-artifacts';
  const isPreRequisites = safeLesson.key === 'pre-requisites';
  const isAnalytics = safeLesson.key === 'analytics';
  const hasCaseStudyContext = isCaseStudy || isPromptLabs || isAIArtifacts;
  const attendeeid = getLoggedInAttendeeId();
  const [userRole, setUserRole] = useState('');
  const [moduleApiPayload, setModuleApiPayload] = useState(null);
  const [moduleDataStatus, setModuleDataStatus] = useState('idle'); // idle | loading | loaded
  const [caseStudies, setCaseStudies] = useState([]);
  const [activeCaseStudy, setActiveCaseStudy] = useState(null);
  const [caseStudyError, setCaseStudyError] = useState('');
  const [caseStudyRefresh, setCaseStudyRefresh] = useState(0);
  const [isCreatingCaseStudy, setIsCreatingCaseStudy] = useState(false);

  // Auto-complete lessons that have no interactive gate (view-only content)
  const VIEW_ONLY_KEYS = ['introduction', 'pre-requisites', 'recording', 'important-link', 'analytics'];
  useEffect(() => {
    if (safeLesson.key && VIEW_ONLY_KEYS.includes(safeLesson.key)) {
      onLessonComplete?.(safeLesson.key);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safeLesson.key]);

  useEffect(() => {
    const controller = new AbortController();
    const eventid = parseInt(workshopId, 10) || 0;
    if (!eventid || !attendeeid) {
      setCaseStudies([]);
      setActiveCaseStudy(null);
      setModuleApiPayload({ data: [] });
      setModuleDataStatus('loaded');
      return;
    }

    setModuleDataStatus('loading');
    setCaseStudyError('');

    fetch(`${BASE_URL}/workshops/workbench/case-studies?eventid=${eventid}`, {
      headers: getWorkshopHeaders(),
      signal: controller.signal,
    })
      .then(async (res) => {
        const payload = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(payload.error || `Failed to load case studies (${res.status})`);
        return payload;
      })
      .then((payload) => {
        const records = Array.isArray(payload?.data) ? payload.data : [];
        const selected = records.find((record) => Boolean(record.isselected)) || records[0] || null;
        setCaseStudies(records);
        setActiveCaseStudy(selected);
        setModuleApiPayload({ ...payload, data: records });
        setModuleDataStatus('loaded');
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        setCaseStudies([]);
        setActiveCaseStudy(null);
        setModuleApiPayload({ data: [] });
        setModuleDataStatus('loaded');
        setCaseStudyError(error.message || 'Case studies could not be loaded.');
      });
      return () => controller.abort();
  }, [workshopId, attendeeid, caseStudyRefresh]);

  const activateCaseStudy = async (scenarioid) => {
    const previous = activeCaseStudy;
    const next = caseStudies.find((record) => Number(record.id) === Number(scenarioid));
    if (!next || Number(previous?.id) === Number(next.id)) return;

    setModuleDataStatus('loading');
    setCaseStudyError('');
    try {
      const response = await fetch(`${BASE_URL}/workshops/workbench/case-studies/${next.id}/activate`, {
        method: 'POST',
        headers: getWorkshopHeaders(true),
        body: JSON.stringify({ eventid: parseInt(workshopId, 10) }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || `Failed to activate case study (${response.status})`);

      const updated = caseStudies.map((record) => ({
        ...record,
        isselected: Number(record.id) === Number(next.id) ? 1 : 0,
      }));
      setCaseStudies(updated);
      setActiveCaseStudy({ ...next, isselected: 1 });
      setModuleApiPayload((current) => ({ ...(current || {}), data: updated }));
      setIsCreatingCaseStudy(false);
    } catch (error) {
      setActiveCaseStudy(previous);
      setCaseStudyError(error.message || 'The active case study could not be changed.');
    } finally {
      setModuleDataStatus('loaded');
    }
  };

  const archiveActiveCaseStudy = async () => {
    if (!activeCaseStudy?.id) return;
    if (!window.confirm(`Archive "${activeCaseStudy.title}"?`)) return;
    setModuleDataStatus('loading');
    setCaseStudyError('');
    try {
      const eventid = parseInt(workshopId, 10);
      const response = await fetch(
        `${BASE_URL}/workshops/workbench/case-studies/${activeCaseStudy.id}?eventid=${eventid}`,
        { method: 'DELETE', headers: getWorkshopHeaders() }
      );
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || `Failed to archive case study (${response.status})`);
      setIsCreatingCaseStudy(false);
      setCaseStudyRefresh((value) => value + 1);
    } catch (error) {
      setModuleDataStatus('loaded');
      setCaseStudyError(error.message || 'The case study could not be archived.');
    }
  };

  const handleCaseStudySaved = () => {
    setIsCreatingCaseStudy(false);
    setCaseStudyRefresh((value) => value + 1);
    onLessonComplete?.(safeLesson.key);
  };

  const renderBlocks = useMemo(
    () => buildWorkshopRenderBlocks(moduleKey, safeLesson.key, moduleApiPayload, WORKSHOP_MODULE_CONTENT_CONFIG),
    [moduleKey, safeLesson.key, moduleApiPayload]
  );

  const resolvedCaseStudy = activeCaseStudy || {};

  return (
    <main className="wk-workarea-panel">
      <div className="wk-workarea-header">
        <span className="wk-workarea-module">{moduleTitle}</span>
        <h2 className="wk-workarea-title">{safeLesson.title}</h2>
        <p className="wk-workarea-objective">{safeLesson.objective}</p>
      </div>

      <div className="wk-workarea-body">
        {hasCaseStudyContext ? (
          <section className="wk-case-study-manager" aria-label="Active case study">
            <div className="wk-case-study-manager-main">
              <label className="wk-form-label" htmlFor="wk-active-case-study">Active Case Study</label>
              <select
                id="wk-active-case-study"
                className="wk-form-input wk-case-study-select"
                value={activeCaseStudy?.id || ''}
                onChange={(event) => activateCaseStudy(event.target.value)}
                disabled={moduleDataStatus === 'loading' || caseStudies.length === 0}
              >
                <option value="">No case study selected</option>
                {caseStudies.map((record) => (
                  <option key={record.id} value={record.id}>{record.title}</option>
                ))}
              </select>
            </div>
            <div className="wk-case-study-manager-actions">
              {isCaseStudy ? (
                <button
                  type="button"
                  className="ep-btn-ghost ep-btn-sm"
                  onClick={() => setIsCreatingCaseStudy((value) => !value)}
                >
                  {isCreatingCaseStudy ? 'Cancel New' : 'New Case Study'}
                </button>
              ) : null}
              <button
                type="button"
                className="ep-btn-ghost ep-btn-sm wk-case-study-archive"
                onClick={archiveActiveCaseStudy}
                disabled={!activeCaseStudy || moduleDataStatus === 'loading'}
              >
                Archive
              </button>
            </div>
            {moduleDataStatus === 'loading' ? (
              <p className="wk-form-loading" role="status">Updating case studies…</p>
            ) : null}
            {moduleDataStatus === 'loaded' && caseStudies.length === 0 && !caseStudyError ? (
              <p className="wk-form-help">No case studies yet. Create one in the Case Study lesson.</p>
            ) : null}
            {caseStudyError ? <p className="wk-form-error" role="alert">{caseStudyError}</p> : null}
          </section>
        ) : null}

        {renderBlocks.survey ? (
          <section className="wk-content-card">
            <h3>{renderBlocks.survey.title || 'Survey'}</h3>
            {renderBlocks.survey.description ? (
              <p>{renderBlocks.survey.description}</p>
            ) : null}
            <SurveyWidget surveyConfig={renderBlocks.survey} onSubmitted={() => onLessonComplete?.(safeLesson.key)} />
          </section>
        ) : null}

        {isCaseStudy ? (
          <section className="wk-content-card">
            <h3>Case Study Submission</h3>
            <p>
              Capture your brief with business context, personas, and success criteria.
              Saved responses are loaded automatically when available.
            </p>
            <CaseStudyForm
              workshopId={workshopId}
              caseStudy={isCreatingCaseStudy ? null : activeCaseStudy}
              onSaved={handleCaseStudySaved}
            />
          </section>
        ) : isPromptLabs ? (
          <section className="wk-content-card">
            <h3>5W Prompt Labs</h3>
            <p>
              Work through Why, What, Who, When, and Where directly in this workshop page.
            </p>
            <PromptLabsInline workshopId={workshopId} caseStudy={resolvedCaseStudy} userRole={userRole} onRoleChange={setUserRole} onLessonComplete={() => onLessonComplete?.(safeLesson.key)} />
          </section>
        ) : isAIArtifacts ? (
          <AIArtifactsInline userRole={userRole} caseStudy={resolvedCaseStudy} workshopId={workshopId} onLessonComplete={() => onLessonComplete?.(safeLesson.key)} />
        ) : isPreRequisites ? (
          <section className="wk-content-card">
            <h3>Prerequisites — Installation &amp; Verification</h3>
            <PreRequisitesContent />
          </section>
        ) : isAnalytics ? (
          <section className="wk-content-card wk-coming-soon-card">
            <div className="wk-coming-soon">
              <span className="wk-coming-soon-icon" aria-hidden="true">&#128200;</span>
              <h3 className="wk-coming-soon-title">Coming Soon</h3>
              <p className="wk-coming-soon-desc">
                Analytics for this workshop are being built. Check back soon for insights,
                participation trends, and outcome metrics.
              </p>
            </div>
          </section>
        ) : (
           <p></p>
        )}

 

        <section className="wk-content-card">
          <h3>Lesson Checklist</h3>
          <ul className="wk-checklist">
            {safeChecklist.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </section>
      </div>
    </main>
  );
};

export default WorkshopWorkArea;